import React from 'react';

import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  EditButton,
  Filter,
  maxLength,
  minLength,
  ReferenceArrayInput,
  ReferenceField,
  required,
  TextField,
  ReferenceInput,
  TextInput,
  NumberInput,
  useTranslate,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import EmployeeChipsField from '../employee/EmployeeChipsField';
import ScheduleTypeChipsField from '../schedule_type/ScheduleTypeChipsField';
import EmojiInput from '../../EmojiInput';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import CustomTitle from '../../components/CustomTitle';
import ReferenceArrayChild from '../../components/ReferenceArrayChild';
import {
  formatScheduleTypeName,
  perPage,
  getZ3PConfigValue,
} from '../../helper';
import ReferenceChild from '../../components/ReferenceChild';
import ListWithCSVImport from '../../components/ListWithCSVImport';

const ServiceBayFilter = (props) => (
  <Filter {...props}>
    <BooleanInput source="is_active" defaultValue />
  </Filter>
);

const listStyles = {
  nb_commands: { color: 'purple' },
  center: { textAlign: 'center' },
};

export const ServiceBayList = withStyles(listStyles)(
  ({ classes, ...props }) => {
    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
    return (
      <div>
        <ListWithCSVImport
          {...props}
          filter={{ is_service_bay: true }}
          filters={<ServiceBayFilter />}
          bulkActionButtons={<BulkDeleteButton />}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={50}
        >
          <ZippityDatagrid>
            <TextField source="created_at" />
            <TextField source="modified_at" />
            <TextField source="name" />
            <TextField source="emoji" />
            <EmployeeChipsField source="employees" />
            {!isSkippingVehicle ? (
              <ScheduleTypeChipsField
                source="default_schedule_types"
                sortable={false}
              />
            ) : (
              <></>
            )}
            <ReferenceField
              source="home_client_location_id"
              reference="client-location"
              sortable={false}
              allowEmpty
            >
              <TextField source="long_name" />
            </ReferenceField>
            <TextField source="default_buffer_time_minutes" />
            <BooleanField source="is_active" />
            <EditButton basePath="service-bay" />
          </ZippityDatagrid>
        </ListWithCSVImport>
      </div>
    );
  },
);

const editStyles = {
  referenceArrayWide: { width: '100%' },
};

const validateName = [required(), minLength(2), maxLength(100)];
const validateEmoji = [required(), minLength(1), maxLength(10)];

export const ServiceBayEdit = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const translate = useTranslate();
    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
    return (
      <Edit
        undoable={false}
        title={<CustomTitle title="resources.service-bay.title" />}
        {...props}
      >
        <ZippitySimpleForm>
          <TextInput validate={validateName} source="name" />
          <EmojiInput validate={validateEmoji} source="emoji" />
          {!isSkippingVehicle ? (
            <ReferenceArrayInput
              source="default_schedule_type_ids"
              reference="schedule-type"
              perPage={100}
              className={classes.referenceArrayWide}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <ReferenceArrayChild
                {...props}
                optionText={(option) =>
                  formatScheduleTypeName(option.name, translate)
                }
                source="default_schedule_type_ids"
              />
            </ReferenceArrayInput>
          ) : (
            <></>
          )}
          <ReferenceArrayInput
            source="employee_ids"
            reference="employee"
            perPage={perPage}
            className={classes.referenceArrayWide}
            sort={{ field: 'email_address', order: 'ASC' }}
          >
            <ReferenceArrayChild
              {...props}
              optionText="email_address"
              source="employee_ids"
            />
          </ReferenceArrayInput>
          <ReferenceInput
            label="Shop*"
            source="home_client_location_id"
            reference="client-location"
            sort={{ field: 'long_name', order: 'ASC' }}
            filter={{ is_active: true }}
            perPage={perPage}
            validate={[required()]}
            style={{ width: '100%' }}
          >
            <ReferenceChild
              {...props}
              optionText="long_name"
              source="home_client_location_id"
            />
          </ReferenceInput>
          <NumberInput
            source="default_buffer_time_minutes"
            fullWidth
            helperText="Add additional time to each appointment to account for flexibility, setting up, traffic and more"
          />
          <BooleanInput source="is_active" />
        </ZippitySimpleForm>
      </Edit>
    );
  },
);

export const ServiceBayCreate = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const translate = useTranslate();
    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
    return (
      <Create {...props}>
        <ZippitySimpleForm redirect="/service-bay">
          <TextInput validate={validateName} source="name" />
          <EmojiInput validate={validateEmoji} source="emoji" />
          {!isSkippingVehicle ? (
            <ReferenceArrayInput
              source="default_schedule_type_ids"
              reference="schedule-type"
              perPage={100}
              className={classes.referenceArrayWide}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <ReferenceArrayChild
                {...props}
                optionText={(option) =>
                  formatScheduleTypeName(option.name, translate)
                }
                source="default_schedule_type_ids"
              />
            </ReferenceArrayInput>
          ) : (
            <></>
          )}
          <ReferenceArrayInput
            source="employee_ids"
            reference="employee"
            perPage={perPage}
            className={classes.referenceArrayWide}
            sort={{ field: 'email_address', order: 'ASC' }}
          >
            <ReferenceArrayChild
              {...props}
              optionText="email_address"
              source="employee_ids"
            />
          </ReferenceArrayInput>
          <ReferenceInput
            label="Shop*"
            source="home_client_location_id"
            reference="client-location"
            sort={{ field: 'long_name', order: 'ASC' }}
            filter={{ is_active: true }}
            perPage={perPage}
            validate={[required()]}
            style={{ width: '100%' }}
          >
            <ReferenceChild
              {...props}
              optionText="long_name"
              source="home_client_location_id"
            />
          </ReferenceInput>
          <NumberInput
            source="default_buffer_time_minutes"
            fullWidth
            helperText="Add additional time to each appointment to account for flexibility, setting up, traffic and more"
          />
          <BooleanInput source="is_active" defaultValue />
        </ZippitySimpleForm>
      </Create>
    );
  },
);
